import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, ModalWrapper, ModalCloseButton, Pagination } from '../common';
import { reviewService } from '../../api/services';
import { makeServiceLabels } from '../../constants/serviceLabels';
import { useDeepLTranslated } from '../../hooks/useDeepLTranslated';

const REVIEWS_PER_PAGE = 4;

export default function ReviewListModal({ onClose }) {
  const { t } = useTranslation();
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('latest');
  const [sortOpen, setSortOpen] = useState(false);
  const sortRef = useRef(null);
  const [dummyReviews, setDummyReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);

  useEffect(() => {
    if (!sortOpen) return;
    const handleClick = (e) => { if (!sortRef.current?.contains(e.target)) setSortOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [sortOpen]);

  useEffect(() => {
    reviewService.getStats().then((res) => {
      if (res.average_rating) setAvgRating(res.average_rating);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const serviceLabels = makeServiceLabels(t);
    reviewService.getList({ size: 20, sort }).then((res) => {
      const items = Array.isArray(res) ? res : res.items || [];
      setDummyReviews(items.map((r) => ({
        id: r.id,
        name: r.username ? r.username.charAt(0) + '**' : t('common.anonymous_label'),
        date: r.created_at ? new Date(r.created_at).toLocaleDateString('ko-KR', { year: 'numeric', month: '2-digit', day: '2-digit' }).replace(/\./g, '.').replace(/\s/g, '') : '',
        service: (r.service_type && serviceLabels[r.service_type]) || r.service_label || t('common.used_service'),
        vehicle: r.vehicle_label || '',
        rating: r.rating || 0,
        text: r.content,
      })));
      setPage(1);
    }).catch((e) => console.warn(e.message));
  }, [sort]);

  const totalPages = Math.ceil(dummyReviews.length / REVIEWS_PER_PAGE);
  const currentReviews = dummyReviews.slice(
    (page - 1) * REVIEWS_PER_PAGE,
    page * REVIEWS_PER_PAGE
  );

  // Reviews.js 와 동일 패턴 — text/vehicle/service 인터리브 번들로 DeepL 일괄 번역
  const reviewTextsBundle = useMemo(() => {
    const out = [];
    currentReviews.forEach((r) => { out.push(r.text || '', r.vehicle || '', r.service || ''); });
    return out;
  }, [currentReviews]);
  const tReviewTexts = useDeepLTranslated(reviewTextsBundle);

  return (
    <ModalWrapper maxWidth="modal-md" padding="" gap="" className="overflow-hidden sm:h-[1000px]">
      {/* Header */}
      <div className="flex items-center justify-between px-5 sm:px-8 pt-5 sm:pt-8 pb-3">
        <h2 className="text-xl font-bold text-mono-900">{t('common.review_list_title')}</h2>
        <ModalCloseButton onClick={onClose} size="sm" />
      </div>

      {/* Rating + Sort */}
      <div className="flex items-center justify-between px-5 sm:px-8 pb-4">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5">
            {[1, 2, 3, 4, 5].map((star) => (
              <Icon key={star} name="star-filled" size={18} className="text-star" />
            ))}
          </div>
          <span className="text-base font-bold text-mono-900">{t('common.avg_format', { value: avgRating })}</span>
        </div>
        <div className="relative" ref={sortRef}>
          <button
            type="button"
            onClick={() => setSortOpen((v) => !v)}
            className="flex items-center gap-1 text-sm text-mono-500 hover:text-mono-900 transition"
          >
            {sort === 'latest' ? t('common.sort_latest') : t('common.sort_rating')}
            <Icon name="chevron-down" size={14} className={`text-mono-400 transition-transform duration-200 ${sortOpen ? 'rotate-180' : ''}`} />
          </button>
          {sortOpen && (
            <div className="absolute right-0 top-full mt-1.5 bg-white border border-mono-200 rounded-xl shadow-lg overflow-hidden z-50 min-w-[90px]">
              {[{ value: 'latest', label: t('common.sort_latest') }, { value: 'rating', label: t('common.sort_rating') }].map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => { setSort(opt.value); setSortOpen(false); }}
                  className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${sort === opt.value ? 'bg-orange-50 text-orange-500 font-semibold' : 'text-mono-900 hover:bg-orange-50/50'}`}
                >
                  {opt.label}
                  {sort === opt.value && <Icon name="check" size={14} className="text-orange-500" />}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Review List */}
      <div className="px-5 sm:px-8 flex flex-col gap-3 overflow-y-auto flex-1 min-h-0">
        {currentReviews.map((review, i) => {
          const tText = tReviewTexts[i * 3] || review.text;
          const tVehicle = tReviewTexts[i * 3 + 1] || review.vehicle;
          const tService = tReviewTexts[i * 3 + 2] || review.service;
          return (
          <div key={review.id} className="bg-mono-50 rounded-xl p-4 sm:p-5">
            {/* User info */}
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-mono-900">{review.name}</p>
              <p className="text-xs text-mono-500">{review.date}</p>
            </div>

            {/* Star rating */}
            <div className="flex items-center gap-0.5 mb-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Icon
                  key={star}
                  name={star <= review.rating ? 'star-filled' : 'star'}
                  size={14}
                  className={star <= review.rating ? 'text-star' : 'text-mono-300'}
                />
              ))}
            </div>

            {/* Service info */}
            <div className="flex flex-col gap-0.5 mb-3">
              <span className="text-xs font-bold text-mono-500">{t('common.used_service')}</span>
              <span className="text-xs text-mono-900">{tService}</span>
              <span className="text-xs text-mono-900">{tVehicle}</span>
            </div>

            {/* Review text */}
            <p className="text-sm text-mono-900 leading-relaxed whitespace-pre-line">
              {tText}
            </p>
          </div>
          );
        })}
      </div>

      {/* Pagination */}
      <div className="px-5 sm:px-8 py-4 sm:py-5">
        <Pagination current={page} total={totalPages} onChange={setPage} />
      </div>
    </ModalWrapper>
  );
}
